// `/auth/mfa/replace`: replacing a lost authenticator app (#746).
//
// Read off `control_plane/routes/auth.py`. **The act is three calls**: a
// step-up for `mfa_replace` (the one factor act a recovery code may prove),
// `POST /auth/mfa/replace` with the grant, which answers with the new secret,
// and `POST /auth/mfa/replace/verify` with a code from it, which swaps it in
// and answers with the new recovery codes, once. Until the verify, the old
// authenticator and recovery codes stand; the server holds the new secret
// beside them.

import type { Api } from "./http";
import { beginPasskeyAuthentication, mintStepUpGrant, readMfaStatus, type MfaStatus, type PasskeyChallenge } from "./passkeys";
import type { StepUp } from "./policy";

export const MFA_SETUP_PATH = "/auth/mfa/setup";
export const MFA_VERIFY_PATH = "/auth/mfa/verify";
/** `auth/service.py::MFA_SETUP_PURPOSE`: the step-up an enrolment rides (#743). */
export const MFA_SETUP_PURPOSE = "mfa_setup";
export const MFA_REPLACE_PATH = "/auth/mfa/replace";
export const MFA_REPLACE_VERIFY_PATH = "/auth/mfa/replace/verify";
/** `auth/service.py::MFA_REPLACE_PURPOSE`: the step-up a replacement rides (#746). */
export const MFA_REPLACE_PURPOSE = "mfa_replace";

/**
 * Start an enrolment (#875): a fresh secret, pending until a code proves it. A person
 * with no password and no factor has no grant to send; the server lets a fresh
 * session stand in for it (#743).
 */
export const beginSetup = (api: Api, grant: string | null): Promise<Replacement> =>
  api.post<Replacement>(MFA_SETUP_PATH, { step_up_grant: grant });

/** Finish it with a code from the app: the recovery codes, shown once. */
export const verifySetup = async (api: Api, code: string): Promise<string[]> =>
  (await api.post<{ recovery_codes: string[] }>(MFA_VERIFY_PATH, { code })).recovery_codes;

/** The new secret, to type or scan into the new authenticator app. */
export interface Replacement {
  secret: string;
  /** The `otpauth://` URI an authenticator app reads. */
  qr_uri: string;
}

export const beginReplacement = (api: Api, grant: string): Promise<Replacement> =>
  api.post<Replacement>(MFA_REPLACE_PATH, { step_up_grant: grant });

/** Finish it with a code from the new app: the new recovery codes, shown once. */
export const verifyReplacement = async (api: Api, code: string): Promise<string[]> =>
  (await api.post<{ recovery_codes: string[] }>(MFA_REPLACE_VERIFY_PATH, { code }))
    .recovery_codes;

/** The calls Your account makes to set up or replace the authenticator, bound to one `Api`. */
export interface AuthenticatorActions {
  readMfa(): Promise<MfaStatus>;
  beginPasskey(purpose: string): Promise<PasskeyChallenge>;
  stepUp(proof: StepUp, purpose: string): Promise<string>;
  begin(grant: string): Promise<Replacement>;
  verify(code: string): Promise<string[]>;
  /** Set one up where there is none (#875). */
  beginSetup(grant: string | null): Promise<Replacement>;
  verifySetup(code: string): Promise<string[]>;
}

export const authenticatorActions = (api: Api): AuthenticatorActions => ({
  readMfa: () => readMfaStatus(api),
  beginPasskey: (purpose) => beginPasskeyAuthentication(api, purpose),
  stepUp: (proof, purpose) => mintStepUpGrant(api, proof, purpose),
  begin: (grant) => beginReplacement(api, grant),
  verify: (code) => verifyReplacement(api, code),
  beginSetup: (grant) => beginSetup(api, grant),
  verifySetup: (code) => verifySetup(api, code),
});
